import { useQueries } from "@tanstack/react-query";
import { localizationApi } from "@/lib/api/localization";
import { useI18n } from "@/context/I18nContext";

/**
 * Resolves a list of industry IDs and a list of category IDs to their
 * localized display names by calling the single-item endpoints in parallel.
 *
 * Only fetches the IDs that are actually provided — no full-list download.
 * Falls back to the raw ID while loading or when the server returns 404.
 */
export function useLocalizedNames(industryIds: string[], categoryIds: string[]) {
  const { locale } = useI18n();

  const industryQueries = useQueries({
    queries: industryIds.map((id) => ({
      queryKey: ["industry", id, locale],
      queryFn: () => localizationApi.getIndustryById(id, locale),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const categoryQueries = useQueries({
    queries: categoryIds.map((id) => ({
      queryKey: ["category", id, locale],
      queryFn: () => localizationApi.getCategoryById(id, locale),
      staleTime: 5 * 60 * 1000,
    })),
  });

  function resolveIndustryName(id: string): string {
    const idx = industryIds.indexOf(id);
    return industryQueries[idx]?.data?.name ?? id;
  }

  function resolveCategoryName(id: string): string {
    const idx = categoryIds.indexOf(id);
    return categoryQueries[idx]?.data?.name ?? id;
  }

  return { resolveIndustryName, resolveCategoryName };
}
